import { useEffect } from "react";

/**
 * One-shot scroll reveal for every `[data-reveal]` inside `root`.
 *
 * Items entering together stagger by 70ms so a row of cards cascades rather
 * than popping in as a block. Each element is unobserved once it fires, and a
 * MutationObserver picks up nodes that mount later (accordion, menu).
 */
export function useReveal(root: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = root.current;

    if (!el || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (!entry.isIntersecting) {
            return;
          }

          const node = entry.target as HTMLElement;
          node.style.animationDelay = `${(index % 4) * 70}ms`;
          node.classList.add("is-in");
          observer.unobserve(node);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const scan = () => {
      el.querySelectorAll("[data-reveal]:not(.is-in)").forEach((node) => observer.observe(node));
    };

    scan();

    const mutations = new MutationObserver(scan);
    mutations.observe(el, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [root]);
}
